import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const _Carousel = ({ featuredPost }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!featuredPost?.length) return;

    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % featuredPost.length);
        setFade(true);
      }, 250);
    }, 8000);

    return () => clearInterval(interval);
  }, [featuredPost]);

  if (!featuredPost?.length) {
    return (
      <div className="text-white text-center p-6">
        Aucun post en vedette n'est disponible.
      </div>
    );
  }

  // Normalise les deux formats de path (Laravel legacy et .NET)
  const imgUrl = (path) => {
    if (!path) return null;
    const base = process.env.REACT_APP_IMG_URL?.replace(/\/$/, "");
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalized}`;
  };

  const currentPost = featuredPost[currentIndex];

  // Préférer thumbnail (.NET) sur image (Laravel legacy)
  const coverImage = currentPost.thumbnail || currentPost.image;

  const changeSlide = (newIndex) => {
    setFade(false);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setFade(true);
    }, 250);
  };

  const goToPrev = () =>
    changeSlide(currentIndex === 0 ? featuredPost.length - 1 : currentIndex - 1);

  const goToNext = () =>
    changeSlide((currentIndex + 1) % featuredPost.length);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-10">
      <div className={`transition-opacity duration-300 ${fade ? "opacity-100" : "opacity-0"}`}>
        <div className="relative rounded-3xl overflow-hidden bg-[#111] shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_0.9fr] lg:h-[580px]">

            {/* Image */}
            <div className="relative h-[280px] sm:h-[360px] lg:h-full bg-black">
              <img
                src={imgUrl(coverImage)}
                alt={currentPost.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-black/5 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/10" />
            </div>

            {/* Panneau texte */}
            <div className="bg-[#111] border-l border-white/5">
              <div className="h-full p-6 md:p-8 lg:p-10 grid grid-rows-[auto_1fr_auto] gap-6 lg:h-[580px]">

                {/* Haut */}
                <div>
                  <p className="uppercase tracking-[0.3em] text-xs text-orange-500 mb-4">
                    Sélection
                  </p>
                  <h2 className="text-white text-2xl md:text-3xl font-extrabold leading-tight line-clamp-2">
                    {currentPost.title}
                  </h2>
                </div>

                {/* Centre */}
                <div className="min-h-0 overflow-hidden">
                  <p className="text-gray-300 text-sm md:text-base leading-relaxed line-clamp-4 lg:line-clamp-5">
                    {currentPost.description}
                  </p>
                </div>

                {/* Bas */}
                <div className="pt-2">
                  <div className="flex flex-wrap items-center gap-4">
                    <Link
                      to={`/posts/${currentPost.slug}`}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold transition"
                    >
                      Voir l'article
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                    <span className="text-sm text-gray-400">
                      {currentIndex + 1} / {featuredPost.length}
                    </span>
                  </div>

                  <div className="flex gap-2 mt-6">
                    {featuredPost.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => changeSlide(index)}
                        className={`h-2.5 rounded-full transition-all duration-300 ${
                          index === currentIndex
                            ? "w-8 bg-orange-500"
                            : "w-2.5 bg-gray-600 hover:bg-gray-400"
                        }`}
                        aria-label={`Aller au slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flèche gauche */}
          <button
            onClick={goToPrev}
            className="absolute left-4 top-[140px] sm:top-[180px] lg:top-1/2 lg:-translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition"
            aria-label="Précédent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Flèche droite */}
          <button
            onClick={goToNext}
            className="absolute right-4 top-[140px] sm:top-[180px] lg:top-1/2 lg:-translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition"
            aria-label="Suivant"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default _Carousel;